// Member.js - OPTIMIZED VERSION
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/Member.css";
import { BASE_URL } from "../Apis/apiConfig";
import { Users, Search, RefreshCw, Edit, Trash2, CheckSquare, X, FileX, AlertCircle, UserPlus, Mail, Building2, Eye,
} from "lucide-react";
import Swal from "sweetalert2";
import Modal from "react-modal";
import axios from "axios";
import * as XLSX from "xlsx-js-style";

Modal.setAppElement("#root");

const Member = () => {
  const navigate = useNavigate();
  const [memberList, setMemberList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editMemberData, setEditMemberData] = useState(null);
  const [selectedWing, setSelectedWing] = useState("");
  const [wings, setWings] = useState([]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const token = localStorage.getItem("token");
  const society = (
    localStorage.getItem("societyName") ||
    localStorage.getItem("society") ||
    ""
  ).trim();

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!token) throw new Error("Not logged in");
      if (!society) throw new Error("Society not set");

      const response = await axios.get(`${BASE_URL}/api/member/get-list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { society },
        validateStatus: null,
      });

      if (response.status === 200) {
        let payload = response.data;

        if (payload == null) {
          setMemberList([]);
          setError("Please upload Excel only"); 
        } else if (Array.isArray(payload)) {
          setMemberList(payload);
        } else if (Array.isArray(payload.data)) {
          setMemberList(payload.data);
        } else {
          setMemberList([]);
        }
      } else if (response.status === 403) {
        setMemberList([]);
        setError("Please upload Excel only");
      } else {
        throw new Error(
          response.data?.message ||
          `Failed to fetch members (status ${response.status})`,
        );
      }
    } catch (err) {
      console.error("fetchMembers error:", err);
      if (!error) setError(err.message || "Failed to fetch members");
      setMemberList([]);

      Swal.fire({
        icon: "error",
        title: "Failed to load members",
        text: err.message || "Please check your connection and try again.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  }, [society, token]);


  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    if (memberList.length) {
      const uniqueWings = [
        ...new Set(memberList.map(getWing).filter(Boolean)),
      ].sort();

      setWings(uniqueWings);
    }
  }, [memberList]);

  const getWing = (member) => {
    if (member.wing || member.Wing) {
      return String(member.wing || member.Wing).trim();
    }

    const unit = member.unit_no || member.Unit_No || "";
    const match = String(unit).match(/^[A-Za-z]+/);
    return match ? match[0] : "";
  };

  // Filter members
  const filteredMembers = useMemo(() => {
    let filtered = memberList;

    if (selectedWing) {
      filtered = filtered.filter((member) => getWing(member) === selectedWing);
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((member) =>
        Object.values(member).some((value) =>
          String(value).toLowerCase().includes(searchLower),
        ),
      );
    }

    return filtered;
  }, [memberList, selectedWing, searchTerm]);

  // Pagination
  const totalItems = filteredMembers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [perPage, totalItems, currentPage, totalPages]);

  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredMembers.slice(start, start + perPage);
  }, [filteredMembers, currentPage, perPage]);

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getField = (obj, keys = []) => {
    if (!obj) return "";
    for (const k of keys) {
      const v = obj[k];
      if (v !== undefined && v !== null && String(v).trim() !== "")
        return String(v);
    }
    return "";
  };

  const allKeys = useMemo(() => {
    if (memberList.length > 0) {
      const firstMember = memberList[0];

      const preferredColumns = [
        "member_name",
        "MemberName",
        "name",
        "unit_no",
        "Unit_No",
        "wing",
        "Wing_No",
        "phone_number",
        "Phone",
        "Mobile",
        "phone",
        "email",
        "Email",
        "address",
        "Address",
        "flat_square_feet",
        "Flat_Square_feet",
        "status",
        "Status",
        "pincode",
        "Pin",
        "PostalCode",
      ];

      const allKeys = Object.keys(firstMember);

      const sortedKeys = [
        ...preferredColumns.filter((key) => allKeys.includes(key)),
        ...allKeys.filter(
          (key) =>
            !preferredColumns.includes(key) &&
            ![
              "id",
              "_id",
              "password",
              "createdAt",
              "updatedAt",
              "__v",
              "token",
            ].includes(key),
        ),
      ];

      return sortedKeys.slice(0, 8);
    }
    return [
      "member_name",
      "unit_no",
      "phone_number",
      "email",
      "address",
      "flat_square_feet",
      "status",
      "pincode",
    ];
  }, [memberList]);

  // Render page numbers
  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${totalPages === currentPage ? "active" : ""
              }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }

    return pages;
  };


  const handleExportExcel = () => {
  if (!memberList || memberList.length === 0) {
    Swal.fire("Info", "No data available to export", "info");
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(memberList);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Members");

  XLSX.writeFile(
    workbook,
    `${society}_Members_${new Date().toISOString().split("T")[0]}.xlsx`
  );
};

  const handleEditClick = (member) => {
    const mapped = {
      name: getField(member, ["member_name", "MemberName", "name"]) || "",
      wing: getField(member, ["unit_no", "Wing_No", "flat", "wing"]) || "",
      phone:
        getField(member, ["phone_number", "Phone", "Mobile", "phone"]) || "",
      email: getField(member, ["email", "Email"]) || "",
      address: getField(member, ["address", "Address"]) || "",
      pincode: getField(member, ["pincode", "Pin", "PostalCode"]) || "",
      status: getField(member, ["status", "Status"]) || "",
      SquareFeet:
        getField(member, ["flat_square_feet", "Flat_Square_feet"]) || "",
      _id: member.id || member._id || null,
    };

    setEditMemberData(mapped);
    setIsEditModalOpen(true);
  };

  const handleEditChange = (field, value) => {
    setEditMemberData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editMemberData) return;

    try {
      const params = {
        unitNo: editMemberData.wing,
        memberName: editMemberData.name,
        email: editMemberData.email,
        phone: editMemberData.phone,
        status: editMemberData.status,
        societyName: society,
        address: editMemberData.address,
        pinCode: editMemberData.pincode,
        SquareFeet: editMemberData.SquareFeet,
      };

      const response = await axios.put(
        `${BASE_URL}/api/member/edit-member-by-unit-no`,
        null,
        {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
          params,
          validateStatus: (s) => s < 500,
        },
      );

      if (response.status === 200) {
        await fetchMembers();
        setIsEditModalOpen(false);
        Swal.fire("Success", "Member updated successfully!", "success");
      } else {
        throw new Error(
          response.data?.message || `Save failed (status ${response.status})`,
        );
      }
    } catch (err) {
      console.error("Update error:", err);
      Swal.fire("Error", err.message || "Failed to update member", "error");
    }
  };

 
  const handleDeleteSelected = async () => {
    if (selectedMembers.length === 0) {
      Swal.fire("Info", "Please select members to delete", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `This will delete ${selectedMembers.length} selected member(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const societyName = localStorage.getItem("society") || "";
      const response = await axios.delete(
        `${BASE_URL}/api/member/delete-member-by-mutiple-id`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { societyName, memberIds: selectedMembers.map(Number) },
        },
      );

      Swal.fire("Deleted!", "Selected members have been deleted!", "success");
      await fetchMembers();
      setSelectedMembers([]);
    } catch (err) {
      console.error("Delete selected error:", err);
      Swal.fire("Error", "Failed to delete selected members", "error");
    }
  };

  // Bulk delete all members
  const handleBulkDelete = async () => {
    if (!society) {
      Swal.fire("Error", "Society name is required", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: `This will permanently delete ALL members for "${society}"!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.post(
        `${BASE_URL}/api/member/delete-member-table`,
        null,
        {
          params: { societyName: society },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.fire("Deleted!", "All members have been deleted!", "success");
      await fetchMembers();
      setSelectedMembers([]);
    } catch (err) {
      console.error("Bulk delete error:", err);
      Swal.fire("Error", "Failed to delete members", "error");
    }
  };

  // Download PDF
  const handleDownloadPDF = (member) => {
    const memberName = getField(member, ["member_name", "MemberName", "name"]);
    const unitNo = getField(member, ["unit_no", "Unit_No", "wing", "Wing_No"]);
    navigate("/download-pdf", { state: { memberName, unitNo } });
  };

  return (
    <div className="app-container">
      <style>
        {`
/* ========== SPACE OPTIMIZATION FIXES ========== */
.app-container {
  height: 100vh;
  overflow: hidden !important;
}

.main-layout {
  height: calc(100vh - 64px);
  overflow: hidden !important;
}

.content-area {
  height: 100%;
  overflow-y: auto !important;
  overflow-x: hidden;
  padding-bottom: 20px;
}

.financial-container,
.financial-card {
  height: auto !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 !important;
}

.financial-body {
  height: auto !important;
  overflow: visible !important;
  padding: 0 !important;
}

/* ========== COMPACT TABLE CONTROLS ========== */
.compact-table-controls {
  padding: 8px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  min-height: 44px;
}

.controls-left,
.controls-center,
.controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.controls-center {
  flex: 1;
  justify-content: center;
}

/* Entries Selector - Compact */
.entries-selector label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.entries-select {
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: white;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
  height: 28px;
}

/* Wing Filter Buttons - Compact */
.wing-filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.wing-filter-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.wing-btn {
  padding: 4px 10px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 11px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  height: 26px;
}

.wing-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e0;
}

.wing-btn.active {
  background: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

/* Pagination - Compact */
.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
}

.pagination-nav {
  padding: 4px 10px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  height: 26px;
  transition: all 0.2s;
}

.pagination-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-nav:not(:disabled):hover {
  background: #f1f5f9;
}

.pagination-btn {
  min-width: 26px;
  height: 26px;
  padding: 4px 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover {
  background: #f1f5f9;
}

.pagination-btn.active {
  background: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.pagination-ellipsis {
  padding: 0 4px;
  color: #94a3b8;
  font-size: 11px;
}

.entries-info {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

/* ========== TABLE OPTIMIZATION ========== */
.financial-table-container {
  overflow-x: auto;
  padding: 0;
  margin: 0;
}

.financial-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Compact Headers */
.financial-table th {
  padding: 6px 4px !important;
  text-align: center !important;
  vertical-align: middle;
  white-space: nowrap;
  font-weight: 600;
  font-size: 11px !important;
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  line-height: 1.2 !important;
  height: 32px !important;
}

/* Compact Data Cells */
.financial-table td {
  padding: 5px 4px !important;
  text-align: center !important;
  vertical-align: middle;
  font-size: 11px !important;
  border-bottom: 1px solid #e2e8f0;
  line-height: 1.3 !important;
  height: 36px !important;
}

/* Column Widths */
.financial-table th:nth-child(1),
.financial-table td:nth-child(1) {
  width: 35px !important;
  min-width: 35px !important;
  max-width: 35px !important;
  padding: 5px 2px !important;
}

.financial-table th:nth-child(2),
.financial-table td:nth-child(2) {
  width: 45px !important;
  min-width: 45px !important;
  max-width: 45px !important;
}

.financial-table th:nth-child(3),
.financial-table td:nth-child(3) {
  width: 130px !important;
  padding: 5px 8px !important;
  text-align: left !important;
}

.financial-table th:nth-child(4),
.financial-table td:nth-child(4) {
  width: 70px !important;
}

.financial-table th:nth-child(5),
.financial-table td:nth-child(5) {
  width: 50px !important;
}

.financial-table th:nth-child(6),
.financial-table td:nth-child(6) {
  width: 105px !important;
}

.financial-table th:nth-child(7),
.financial-table td:nth-child(7) {
  width: 160px !important;
  padding: 5px 8px !important;
  text-align: left !important;
}

.financial-table th:nth-child(8),
.financial-table td:nth-child(8) {
  width: 130px !important;
  padding: 5px 8px !important;
  text-align: left !important;
}

.financial-table th:nth-child(9),
.financial-table td:nth-child(9) {
  width: 70px !important;
}

.financial-table th:nth-child(10),
.financial-table td:nth-child(10) {
  width: 70px !important;
}

.financial-table th:nth-child(11),
.financial-table td:nth-child(11) {
  width: 100px !important;
  padding: 4px 2px !important;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin: 0;
}

.action-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  width: 24px;
  height: 24px;
}

.action-icon-btn svg {
  width: 13px;
  height: 13px;
}

.action-icon-btn:hover {
  background-color: #f1f5f9;
}

/* Status Badges */
.financial-table td:nth-child(10) span {
  padding: 2px 6px !important;
  border-radius: 10px !important;
  font-size: 9px !important;
  font-weight: 600;
  display: inline-block;
  line-height: 1.2;
}

/* Checkbox */
.financial-table input[type="checkbox"] {
  margin: 0;
  width: 13px;
  height: 13px;
  vertical-align: middle;
  cursor: pointer;
}

/* Row Hover */
.financial-table tbody tr:hover td {
  background-color: #f8fafc;
}

/* Cell Ellipsis */
.cell-ellipsis {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Empty State */
.financial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 300px;
}

.financial-state h3 {
  margin: 16px 0 8px 0;
  color: #334155;
  font-size: 18px;
  font-weight: 600;
}

.financial-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Edit Modal Styles */
.edit-modal {
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.edit-modal-content {
  padding: 0;
}

.edit-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.edit-modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.edit-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #64748b;
  transition: all 0.2s;
}

.edit-modal-close:hover {
  background: #f1f5f9;
  color: #334155;
}

.edit-modal-body {
  padding: 24px;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.btn-cancel {
  padding: 8px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #cbd5e0;
}

.btn-save {
  padding: 8px 16px;
  background: #0d6efd;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover {
  background: #0b5ed7;
}

/* Responsive */
@media (max-width: 768px) {
  .compact-table-controls {
    flex-direction: column;
    align-items: stretch;
    padding: 10px 16px;
  }
  
  .controls-left,
  .controls-center,
  .controls-right {
    width: 100%;
    justify-content: space-between;
  }
  
  .wing-filter-group {
    flex-wrap: wrap;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
}
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader
            onRefresh={fetchMembers}
            onDeleteAll={handleBulkDelete}
            onDeleteSelected={handleDeleteSelected}
            selectedCount={selectedMembers.length}
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            loading={loading}
          />

          <div className="financial-container ">
            <div className="financial-card ">
              {/* SINGLE COMPACT CONTROL ROW */}
              <div className="flex px-4 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-tr-lg rounded-tl-lg  text-white py-2">
                {/* Left - Entries Selector */}
                <div className="controls-left">
                  <div className="entries-selector ">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>

                {/* Center - Wing Filter */}
                <div className="controls-center ">
                  <div className="wing-filter-group">
                    <span className="wing-filter-label text-white">
                      <Building2 size={12} />
                      Wing:
                    </span>
                    <button
                      className={`wing-btn ${selectedWing === "" ? "active" : ""}`}
                      onClick={() => setSelectedWing("")}
                    >
                      All
                    </button>
                    {wings.slice(0, 8).map((wing) => (
                      <button
                        key={wing}
                        className={`wing-btn ${selectedWing === wing ? "active" : ""
                          }`}
                        onClick={() => setSelectedWing(wing)}
                      >
                        {wing}
                      </button>
                    ))}
                    {wings.length > 8 && (
                      <span style={{ fontSize: "11px", color: "#64748b" }}>
                        +{wings.length - 8}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right - Pagination */}
                <div className="controls-right ">
                  {!loading && totalItems > 0 && (
                    <span className="entries-info text-white">
                      {(currentPage - 1) * perPage + 1}-
                      {Math.min(currentPage * perPage, totalItems)} of{" "}
                      {totalItems}
                    </span>
                  )}

                  <div className="flex gap-2 p-2 ">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>

                    {renderPageNumbers()}

                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
                <button
  onClick={handleExportExcel}
  style={{
    padding: "8px 15px",
    background: "#16a34a",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  }}
>
  Download Excel
</button>
              </div>

              {/* Main Content Area */}
              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Members</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={fetchMembers}
                      style={{
                        marginTop: "20px",
                        padding: "10px 20px",
                        background: "#0d6efd",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="financial-state">
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "40px",
                        borderRadius: "16px",
                        textAlign: "center",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <FileX size={64} color="#94a3b8" />
                      <h3
                        style={{
                          margin: "20px 0 10px 0",
                          color: "#334155",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        No Members Found
                      </h3>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {searchTerm
                          ? "No members match your search criteria."
                          : selectedWing
                            ? `No members found in wing "${selectedWing}".`
                            : "There are no members available for this society."}
                      </p>
                      {(searchTerm || selectedWing) && (
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setSelectedWing("");
                          }}
                          style={{
                            marginTop: "20px",
                            padding: "8px 20px",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="financial-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={
                                selectedMembers.length ===
                                filteredMembers.length &&
                                filteredMembers.length > 0
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMembers(
                                    filteredMembers
                                      .map((member) => member.id || member._id)
                                      .filter((id) => id),
                                  );
                                } else {
                                  setSelectedMembers([]);
                                }
                              }}
                            />
                          </th>
                          <th>SR</th>
                          {allKeys.map((key) => (
                            <th key={key}>
                              {key.toLowerCase().includes("flat_square") ? (
                                <div style={{ lineHeight: "1.1" }}>
                                  <div>Flat</div>
                                  <div>Sq Ft</div>
                                </div>
                              ) : (
                                key.replace(/_/g, " ")
                              )}
                            </th>
                          ))}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedMembers.map((member, index) => (
                          <tr key={member.id || member._id || index}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedMembers.includes(
                                  member.id || member._id,
                                )}
                                onChange={(e) => {
                                  const memberId = member.id || member._id;
                                  if (!memberId) return;

                                  if (e.target.checked) {
                                    setSelectedMembers((prev) => [
                                      ...prev,
                                      memberId,
                                    ]);
                                  } else {
                                    setSelectedMembers((prev) =>
                                      prev.filter((id) => id !== memberId),
                                    );
                                  }
                                }}
                              />
                            </td>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            {allKeys.map((key) => (
                              <td key={key}>
                                {key.toLowerCase().includes("wing")
                                  ? getWing(member)
                                  : member[key] || "-"}
                              </td>
                            ))}
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="action-icon-btn"
                                  onClick={() => handleDownloadPDF(member)}
                                  title="View PDF"
                                >
                                  <Eye size={13} color="#0d6efd" />
                                </button>
                                <button
                                  className="action-icon-btn"
                                  onClick={() => handleEditClick(member)}
                                  title="Edit"
                                >
                                  <Edit size={13} color="#28a745" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Edit Modal */}
          <Modal
            isOpen={isEditModalOpen}
            onRequestClose={() => setIsEditModalOpen(false)}
            className="edit-modal"
            overlayClassName="edit-modal-overlay"
          >
            <div className="edit-modal-content">
              <div className="edit-modal-header">
                <h2 className="edit-modal-title">Edit Member</h2>
                <button
                  className="edit-modal-close"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="edit-modal-body">
                {editMemberData && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveEdit();
                    }}
                    className="edit-form"
                  >
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Name</label>
                        <input
                          type="text"
                          value={editMemberData.name}
                          onChange={(e) =>
                            handleEditChange("name", e.target.value)
                          }
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Wing / Flat No</label>
                        <input
                          type="text"
                          value={editMemberData.wing}
                          onChange={(e) =>
                            handleEditChange("wing", e.target.value)
                          }
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Phone Number</label>
                        <input
                          type="text"
                          value={editMemberData.phone}
                          onChange={(e) =>
                            handleEditChange("phone", e.target.value)
                          }
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          value={editMemberData.email}
                          onChange={(e) =>
                            handleEditChange("email", e.target.value)
                          }
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Square Feet</label>
                        <input
                          type="text"
                          value={editMemberData.SquareFeet}
                          onChange={(e) =>
                            handleEditChange("SquareFeet", e.target.value)
                          }
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Status</label>
                        <input
                          type="text"
                          value={editMemberData.status || "N/A"}
                          className="form-input"
                          style={{ backgroundColor: "#f1f5f9" }}
                        />
                      </div>

                      <div
                        className="form-group"
                        style={{ gridColumn: "span 2" }}
                      >
                        <label className="form-label">Address</label>
                        <input
                          type="text"
                          value={editMemberData.address}
                          onChange={(e) =>
                            handleEditChange("address", e.target.value)
                          }
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Pincode</label>
                        <input
                          type="text"
                          value={editMemberData.pincode}
                          onChange={(e) =>
                            handleEditChange("pincode", e.target.value)
                          }
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        onClick={() => setIsEditModalOpen(false)}
                        className="btn-cancel"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn-save">
                        Save Changes
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </Modal>
        </main>
      </div>
    </div>
  );
};

export default Member;
